import React, { useState } from "react";
import { IconButton, Tooltip, Box, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

import { getNotasByEntityWithUser } from "../../api/CriticalPath/criticalPathNotasApi";
import { jwtDecode } from "jwt-decode";
import { getNoteBadgeState } from "./noteBadgeState";

const EMPTY_BADGE_COLOR = "#AB47BC";
const NOTES_BADGE_COLOR = "#00c2a3";
const BADGE_SIZE = 28;

const NotasBadge = ({
  productId,
  idMuestra,
  idPresupuestoA3Pedido = null,
  notasCount,
  unreadCount = 0,
  openIssueCount = 0,
  onOpenDialog,
  size = BADGE_SIZE,
}) => {
  const theme = useTheme();
  const [preview, setPreview] = useState([]);

  const safeCount = typeof notasCount === "number" ? notasCount : 0;
  const badge = getNoteBadgeState({
    noteCount: safeCount,
    unreadCount,
    openIssueCount,
  });
  const { variant, noteCount } = badge;

  const loadPreview = async () => {
    const token = localStorage.getItem("token");
    const { userId } = jwtDecode(token);

    const notas = await getNotasByEntityWithUser({
      productId,
      idMuestra,
      idPresupuestoA3Pedido,
      userId,
    });

    setPreview(notas);
  };

  const tooltipTitle =
    variant === "empty"
      ? "Sin notas"
      : variant === "unread"
        ? `${noteCount} notas · ${badge.unreadCount} sin leer`
        : variant === "issue"
          ? `${noteCount} notas · ${badge.openIssueCount} incidencias abiertas`
          : `${noteCount} notas · todo leído`;

  const badgeSx = {
    width: size,
    height: size,
    minWidth: size,
    minHeight: size,
    px: noteCount > 9 ? 0.5 : 0,
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: size <= 22 ? "0.62rem" : "0.7rem",
    fontWeight: 800,
    lineHeight: 1,
    flexShrink: 0,
    color:
      variant === "unread" || variant === "normal" || variant === "empty"
        ? theme.palette.common.white
        : theme.palette.warning.dark,
    bgcolor:
      variant === "unread"
        ? theme.palette.error.main
        : variant === "issue"
          ? alpha(
              theme.palette.warning.main,
              theme.palette.mode === "dark" ? 0.42 : 0.28,
            )
          : variant === "normal"
            ? NOTES_BADGE_COLOR
            : alpha(
                EMPTY_BADGE_COLOR,
                theme.palette.mode === "dark" ? 0.85 : 0.92,
              ),
    boxShadow:
      variant === "unread"
        ? `0 0 0 3px ${alpha(theme.palette.error.main, 0.2)}`
        : variant === "issue"
          ? `0 0 0 2px ${alpha(theme.palette.warning.main, 0.16)}`
          : variant === "normal"
            ? `0 0 0 2px ${alpha(NOTES_BADGE_COLOR, 0.25)}`
            : `0 0 0 2px ${alpha(EMPTY_BADGE_COLOR, 0.2)}`,
  };

  return (
    <Tooltip
      title={
        safeCount === 0 ? (
          "No hay notas"
        ) : (
          <Box>
            <Typography sx={{ fontSize: "11px", mb: 0.5, fontWeight: 700 }}>
              {tooltipTitle}
            </Typography>
            {preview.slice(0, 5).map((n) => (
              <Typography key={n.Id} sx={{ fontSize: "11px" }}>
                •{" "}
                {n.Nota.length > 60 ? n.Nota.substring(0, 60) + "..." : n.Nota}
              </Typography>
            ))}
            {preview.length > 5 && (
              <Typography sx={{ fontSize: "11px", fontStyle: "italic" }}>
                + {preview.length - 5} más...
              </Typography>
            )}
          </Box>
        )
      }
      arrow
      onOpen={loadPreview}
    >
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onOpenDialog({
            productId: productId ?? null,
            idMuestra: idMuestra ?? null,
            idPresupuestoA3Pedido: idPresupuestoA3Pedido ?? null,
          });
        }}
        sx={{
          width: size,
          height: size,
          p: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box component="span" sx={badgeSx}>
          {variant === "empty" ? null : noteCount}
        </Box>
      </IconButton>
    </Tooltip>
  );
};

export default NotasBadge;
